/**
 * The underwriting models of a portfolio deal, one section per property.
 *
 * A deal that holds properties in several locations carries its own
 * Sponsor-side and client-side (AREP) models for each property, and the property
 * models roll up to the deal (PortfolioRollupPanel). Each property is a tab
 * labelled with its name and location. Each side of the selected property lists
 * its models and ends in a "+ Add UW Model" button: pick a workbook and it is
 * filed under that property on that side, parsed and mapped like any other
 * model. More than one model may sit on a side (revisions, scenarios); exactly
 * one per property and side is the one the roll-up reads, and the card says
 * which.
 *
 * A property with a sponsor model and no client-side model is shown as a gap and
 * the roll-up treats it as one. Nothing here fills a side in.
 *
 * "+ Add property", at the end of the property tabs, files a property the
 * package did not list (POST /loans/{id}/properties); its models are then added
 * on its own tab.
 *
 * Only a portfolio parent mounts this. A single-property deal keeps the model
 * pair it has always had, with an "Add UW Model" on each side that adds a
 * second property and makes the deal a portfolio (UnderwritingPage).
 */

import { useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ArrowRightLeft,
  Building2,
  ExternalLink,
  Loader2,
  LockKeyhole,
  MapPin,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react'

import { invalidateLoanQueries } from '@/services/loanQueryKeys'
import { portfolioService, refreshAfterPropertyAdded, type NewPropertyInput } from '@/services/portfolio'
import { MAX_WORKBOOK_UPLOAD_BYTES, underwritingWorkbooksService } from '@/services/underwritingWorkbooks'
import { useSideLabels, type SideLabels } from './sideLabels'
import {
  SIDE_ROLES,
  UW_PORTFOLIO_KEY,
  uwPropertyModelsService,
  type PortfolioProperty,
  type PropertyModel,
  type SideRole,
  type WithdrawalEffects,
} from '@/services/uwPropertyModels'
import { useDealDossierStore } from '@/store/dealDossierStore'
import { AddPropertyForm } from './AddPropertyForm'
import { EmptyLine } from './ui'

function errorDetail(err: unknown, fallback: string): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  return typeof detail === 'string' && detail.trim() ? detail : fallback
}

function humanDate(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

const STATUS_STYLE: Record<string, string> = {
  draft: 'border-stone-600 text-stone-400',
  blocked: 'border-red-500/40 text-red-300',
  reviewed: 'border-sky-500/40 text-sky-300',
  promoted: 'border-emerald-500/40 text-emerald-300',
  superseded: 'border-stone-700 text-stone-500',
  rejected: 'border-red-500/40 text-red-300',
}

const SIDE_CAPTION: Record<SideRole, string> = {
  sponsor: 'Sponsor-submitted assumptions',
  underwriter: 'Internal credit view',
}

/** A workbook to add to a property on a side; `useInRollup: false` files it without making it the basis. */
type AddRequest = {
  propertyId: string
  propertyName: string
  role: SideRole
  file: File
  useInRollup?: boolean
}

/** An add as it was made: the request, and the model the roll-up read for that side when it was. */
type PendingAdd = AddRequest & { previous: PropertyModel | null }

/** The property a request was made for, by which what it reports is named when its tab is no longer open. */
type RequestedFor = { propertyId: string; propertyName: string }

/** A filing of a model that is already on the deal (or a move to another property), as it was asked for. */
type PendingAssign = {
  versionId: string
  propertyId: string
  propertyName: string
  role?: SideRole
  /** The property whose tab it was asked on. */
  origin: string | null
  useInRollup?: boolean
}

/** What else a withdrawn model had put on the property record, in words (empty when nothing). */
function tookBackBesidesFigures(done: WithdrawalEffects): string {
  const parts = [
    done.retractedSchedules.length ? `its ${done.retractedSchedules.length === 1 ? 'schedule' : 'schedules'}` : '',
    done.removedComps ? `${done.removedComps} comparable${done.removedComps === 1 ? '' : 's'}` : '',
    done.removedRentRollRows ? `${done.removedRentRollRows} rent-roll row${done.removedRentRollRows === 1 ? '' : 's'}` : '',
  ].filter(Boolean)
  return parts.length ? ` Took back ${parts.join(', ')}.` : ''
}

/** What taking a model off a record takes back, in words. Figures are a Sponsor model's alone. */
function takenBackWords(role: string): string {
  return role === 'sponsor'
    ? 'the figures it filled that nobody has edited since, schedules, comparables and rent roll'
    : 'schedules, comparables and rent roll'
}

/** What moving a model to the other side does, said before it is done. */
function moveSideWords(from: SideRole, labels: SideLabels): string {
  const to = otherSide(from)
  return from === 'sponsor'
    ? `Move this model to the ${labels[to]} side? It stops being a Sponsor model: the roll-up no longer reads it for the Sponsor figures, and figures it filled on the property record that nobody has edited since are taken back.`
    : `Move this model to the ${labels[to]} side? It becomes a Sponsor model: the roll-up may read it for the Sponsor figures, and it fills any figure the property record is missing.`
}

/** What moving a model to another property does, said before it is done. */
function moveToPropertyWords(property: PortfolioProperty, role: SideRole): string {
  return `Move this model to ${property.name}? It is filed there as a new model and removed here, taking back what it put on this property’s record: ${takenBackWords(role)}. The original stays on file as evidence.`
}

function otherSide(role: SideRole): SideRole {
  return role === 'sponsor' ? 'underwriter' : 'sponsor'
}

function isSide(role: string): role is SideRole {
  return role === 'sponsor' || role === 'underwriter'
}

/** The models on one side of a property, newest first as the server lists them. */
function sideModels(property: PortfolioProperty, role: SideRole): PropertyModel[] {
  return property.models.filter((m) => m.role === role)
}

function missingSides(property: PortfolioProperty): SideRole[] {
  return SIDE_ROLES.filter((role) => !property.basis[role])
}

type PanelProps = {
  loanId: string
  unlocked: boolean
  /** The deal's own model pair, kept beneath the properties for whole-portfolio workbooks. */
  dealLevel?: ReactNode
  /** Open a property's own underwriting workspace. */
  onOpenProperty?: (propertyId: string) => void
  /**
   * The deal may gain a property here: an unlocked dossier and a role that may edit the
   * pipeline (a property is a deal of its own). "+ Add property" is offered only then.
   */
  canAddProperty?: boolean
}

/**
 * Everything the panel holds (the property open, a refusal, a notice, the offer to file a refused workbook, and
 * the requests still on their way) is about one portfolio, so each portfolio gets an instance of its own. The
 * dossier keeps this panel mounted as the deal changes, and what one portfolio's request ends with must not
 * reach another's.
 */
export function PortfolioModelsPanel(props: PanelProps) {
  return <PortfolioModels key={props.loanId} {...props} />
}

function PortfolioModels({ loanId, unlocked, dealLevel, onOpenProperty, canAddProperty = false }: PanelProps) {
  const queryClient = useQueryClient()
  const sideLabels = useSideLabels()
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  // The "+ Add property" line, open.
  const [addingProperty, setAddingProperty] = useState(false)
  // A workbook the server refused to make the roll-up model because the model the roll-up
  // reads is approved evidence, which only an approver may replace. It can still be filed
  // alongside that model, so the person is offered that with the same file.
  const [heldAdd, setHeldAdd] = useState<PendingAdd | null>(null)
  // The same for a model filed from the deal, or moved, that was refused for the same reason.
  const [heldAssign, setHeldAssign] = useState<PendingAssign | null>(null)
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([])
  // The property whose tab is open. A request can end after the person has moved to another tab, and what it
  // reports (or offers) is about the property it was made for. Read by the callbacks below when a request ends;
  // written after each render commits, so it is never ahead of what is on screen.
  const onScreen = useRef<string | null>(null)

  const query = useQuery({
    queryKey: UW_PORTFOLIO_KEY(loanId),
    queryFn: () => uwPropertyModelsService.properties(loanId),
    // Read when stale or after a change made here (every writer invalidates it), not on
    // every visit.
    staleTime: 30_000,
  })

  // Every writer refreshes the same things: this section, the roll-up it feeds, and
  // the workbook lists the deal-level pair and each property read (they share one key
  // prefix, see UW_PORTFOLIO_KEY), the deal's journal, where each change is an event, and
  // the deal's record, which carries the Sponsor side's roll-up and its field lineage
  // (Key Deal Metrics, the header, the Audit trail).
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['loan-underwriting-workbooks'] }),
      invalidateLoanQueries(queryClient, loanId),
    ])
  // What a request made for a property says under another property's tab would read as that tab's, so it
  // names the property. `about` is the property the request was made for.
  const said = (message: string, about?: RequestedFor) =>
    about && about.propertyId !== onScreen.current ? `${about.propertyName}: ${message}` : message
  const settle = (message: string | null, about?: RequestedFor) => {
    setError(null)
    setHeldAdd(null)
    setHeldAssign(null)
    setNotice(message === null ? null : said(message, about))
    return refresh()
  }
  const fail = (fallback: string, about?: RequestedFor) => (err: unknown) => {
    setNotice(null)
    setHeldAdd(null)
    setHeldAssign(null)
    setError(said(errorDetail(err, fallback), about))
  }

  const add = useMutation({
    mutationFn: ({ propertyId, role, file, useInRollup }: PendingAdd) =>
      uwPropertyModelsService.add(loanId, propertyId, file, { role, ...(useInRollup === false ? { useInRollup: false } : {}) }),
    onSuccess: (created, vars) => {
      const side = sideLabels[vars.role]
      // The same file is already this property's model on this side: the server filed nothing.
      if (created.reused) {
        return settle(`That file is already ${vars.propertyName}’s ${side} model (${created.displayName}), so nothing was added.`, vars)
      }
      // Whether the roll-up reads the new model is the server's to say, not the request's:
      // asking to file it alongside only leaves it unpinned, and with nothing pinned or
      // approved on the side the newest model is the one read all the same.
      const displaced =
        vars.previous && created.inRollup
          ? ` “${vars.previous.displayName}” is kept but no longer read: use “Use in roll-up” to switch back.`
          : ''
      if (vars.useInRollup === false) {
        return settle(
          created.inRollup
            ? `Added ${created.displayName} to the ${side} side. The roll-up reads it, as the newest model there: none is pinned or approved.${displaced}`
            : `Added ${created.displayName} to the ${side} side. It does not replace the model the roll-up reads: an approver can make it that.`,
          vars,
        )
      }
      return settle(`Added ${created.displayName} to the ${side} side${created.inRollup ? ', now its roll-up basis' : ''}.${displaced}`, vars)
    },
    onError: (err, vars) => {
      fail('That workbook could not be added.', vars)(err)
      const refusedForApproval =
        (err as { response?: { status?: number } })?.response?.status === 403 && /approve/.test(errorDetail(err, ''))
      // The offer files the workbook under the property it was picked on: only while that tab is the one on screen.
      setHeldAdd(refusedForApproval && vars.useInRollup !== false && vars.propertyId === onScreen.current ? vars : null)
    },
  })
  const assign = useMutation({
    mutationFn: ({ versionId, propertyId, role, useInRollup }: PendingAssign) =>
      uwPropertyModelsService.assign(versionId, {
        propertyId,
        ...(role ? { role } : {}),
        ...(useInRollup === false ? { useInRollup: false } : {}),
      }),
    onSuccess: (copy) =>
      settle(
        `Filed ${copy.displayName} under its property.${copy.retractedFields.length ? ` Took back figures the original had filled: ${copy.retractedFields.join(', ')}.` : ''}${tookBackBesidesFigures(copy)}`,
      ),
    onError: (err, vars) => {
      // Named for the property it was to be filed under when the tab it was asked on is no longer open.
      fail('That model could not be assigned to the property.', { propertyId: vars.origin ?? '', propertyName: vars.propertyName })(err)
      // The server refuses an editor who would replace an approved roll-up model and says to file the model
      // alongside it: offered here as it is for an add, on the tab the request was made from.
      const refusedForApproval =
        (err as { response?: { status?: number } })?.response?.status === 403 && /approve/.test(errorDetail(err, ''))
      setHeldAssign(refusedForApproval && vars.useInRollup !== false && vars.origin === onScreen.current ? vars : null)
    },
  })
  const placement = useMutation({
    mutationFn: ({ versionId, body }: { versionId: string; body: { label?: string | null; rollupBasis?: boolean } }) =>
      uwPropertyModelsService.updatePlacement(versionId, body),
    onSuccess: () => settle(null),
    onError: fail('That change could not be saved.'),
  })
  const move = useMutation({
    // The context route replaces the note with whatever it is sent, so the note the model
    // arrived with is sent back: moving a model must not erase where it came from.
    mutationFn: ({ versionId, role, note }: { versionId: string; role: SideRole; note?: string | null }) =>
      underwritingWorkbooksService.setContext(versionId, { role, ...(note ? { note } : {}) }),
    onSuccess: (_updated, vars) => settle(`Moved to the ${sideLabels[vars.role]} side.`),
    onError: fail('That model could not be moved to the other side.'),
  })
  const remove = useMutation({
    mutationFn: ({ versionId }: { versionId: string; name: string }) => uwPropertyModelsService.remove(versionId, undefined),
    onSuccess: (removed, vars) =>
      settle(
        `Removed ${vars.name}. It stays on file as evidence.${removed.retractedFields.length ? ` Took back figures it filled on the property record: ${removed.retractedFields.join(', ')}.` : ''}${tookBackBesidesFigures(removed)}`,
      ),
    onError: fail('That model could not be removed.'),
  })
  // A property the package did not list. Its models are added on its own tab, which opens once
  // the section lists it.
  const addProperty = useMutation({
    mutationFn: (input: NewPropertyInput) => portfolioService.addProperty(loanId, input),
    onSuccess: (added, input) => {
      setAddingProperty(false)
      setError(null)
      setHeldAdd(null)
      setHeldAssign(null)
      if (added.propertyId) setSelectedId(added.propertyId)
      setNotice(
        added.created.length
          ? `Added ${input.name}. Add its Sponsor and ${sideLabels.underwriter} models on its tab.`
          : `${input.name} is already a property of this deal.`,
      )
      return refreshAfterPropertyAdded(queryClient, loanId)
    },
  })
  const busy =
    add.isPending || assign.isPending || placement.isPending || move.isPending || remove.isPending || addProperty.isPending

  const data = query.data
  const properties = useMemo(() => data?.properties ?? [], [data])
  const selected = properties.find((p) => p.id === selectedId) ?? properties[0] ?? null
  useLayoutEffect(() => {
    onScreen.current = selected?.id ?? null
  })
  const locations = useMemo(
    () => new Set(properties.map((p) => (p.location ?? '').trim()).filter(Boolean)).size,
    [properties],
  )

  const open = (propertyId: string) =>
    onOpenProperty ? onOpenProperty(propertyId) : useDealDossierStore.getState().openDossier(propertyId, 'underwriting')

  // A message, and the offer to file a refused workbook another way, are about the property
  // they were raised on: under another property's tab they would read as that one's, and the
  // offer would file the workbook where it was first picked.
  const selectProperty = (propertyId: string) => {
    if (propertyId === selected?.id) return
    setSelectedId(propertyId)
    setError(null)
    setNotice(null)
    setHeldAdd(null)
    setHeldAssign(null)
  }

  // A tablist promises the arrow keys: left and right between the tabs (wrapping), Home and
  // End to the first and last. Selection follows focus, and only the selected tab is in the
  // tab order, as the tabs pattern has it.
  const moveBetweenTabs = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    // Alt+Left and Alt+Right are the browser's back and forward, and Ctrl/Cmd with an arrow its own: not ours.
    if (event.altKey || event.ctrlKey || event.metaKey) return
    const last = properties.length - 1
    const next =
      event.key === 'ArrowRight' ? (index === last ? 0 : index + 1)
        : event.key === 'ArrowLeft' ? (index === 0 ? last : index - 1)
          : event.key === 'Home' ? 0
            : event.key === 'End' ? last
              : null
    if (next === null) return
    event.preventDefault()
    selectProperty(properties[next].id)
    tabRefs.current[next]?.focus()
  }

  if (query.isLoading) {
    return (
      <div className="flex items-center gap-1.5 py-3 text-[10.5px] text-stone-500">
        <Loader2 size={12} className="animate-spin" /> Loading the portfolio's underwriting models…
      </div>
    )
  }
  if (query.isError) {
    return <p className="py-2 text-[10.5px] text-red-300">{errorDetail(query.error, 'Could not load this portfolio’s underwriting models.')}</p>
  }

  // "+ Add property": at the end of the property tabs (not one of them), and the line it opens.
  const addPropertyButton = canAddProperty && !addingProperty ? (
    <button
      type="button"
      disabled={busy}
      onClick={() => {
        addProperty.reset()
        setAddingProperty(true)
      }}
      title="Add a property to this portfolio: one its package did not list"
      className="inline-flex shrink-0 items-center gap-1 rounded border border-amber-400/30 px-2.5 py-1 text-[10px] font-medium text-amber-200 transition-colors hover:bg-amber-400/[0.09] disabled:cursor-not-allowed disabled:border-stone-800 disabled:text-stone-600"
    >
      <Plus size={11} aria-hidden />
      Add property
    </button>
  ) : null
  const addPropertyLine = canAddProperty && addingProperty ? (
    <AddPropertyForm
      label="Add a property"
      heading="Another property for this portfolio"
      busy={addProperty.isPending}
      progress="Adding the property…"
      error={addProperty.isError ? errorDetail(addProperty.error, 'The property could not be added.') : null}
      onSubmit={({ property }) => addProperty.mutate(property)}
      onCancel={() => {
        setAddingProperty(false)
        addProperty.reset()
      }}
    />
  ) : null

  return (
    <section aria-label="Portfolio underwriting models" className="space-y-2.5">
      {properties.length === 0 ? (
        <>
          <EmptyLine>
            This portfolio has no property records yet. Create them from the properties its package lists on the Property tab,
            then add each property’s models here.
          </EmptyLine>
          {addPropertyButton}
          {addPropertyLine}
        </>
      ) : (
        <>
          <p className="text-[9.5px] leading-relaxed text-stone-500">
            {properties.length} propert{properties.length === 1 ? 'y' : 'ies'} in {locations} location{locations === 1 ? '' : 's'}.
            Each carries its own Sponsor and {sideLabels.underwriter} models, and they roll up to this deal.
          </p>

          <div className="flex items-center gap-1.5">
            <div role="tablist" aria-label="Portfolio properties" className="flex min-w-0 gap-1 overflow-x-auto pb-1">
              {properties.map((property, index) => {
                const on = selected?.id === property.id
                const gaps = missingSides(property)
                return (
                  <button
                    key={property.id}
                    ref={(element) => {
                      tabRefs.current[index] = element
                    }}
                    type="button"
                    role="tab"
                    aria-selected={on}
                    tabIndex={on ? 0 : -1}
                    onClick={() => selectProperty(property.id)}
                    onKeyDown={(event) => moveBetweenTabs(event, index)}
                    className={`min-w-[132px] shrink-0 rounded-md border px-2.5 py-1.5 text-left transition-colors ${
                      on
                        ? 'border-amber-300/60 bg-amber-300/[0.07]'
                        : 'border-amber-400/15 bg-black/30 hover:border-amber-400/35'
                    }`}
                  >
                    <span className={`block truncate text-[11px] font-semibold ${on ? 'text-amber-200' : 'text-stone-200'}`}>
                      {property.name}
                    </span>
                    <span className="flex items-center gap-1 text-[9px] text-stone-500">
                      <MapPin size={9} aria-hidden className="shrink-0" />
                      <span className="truncate">{property.location || 'Location not recorded'}</span>
                    </span>
                    {gaps.length > 0 ? (
                      <span className="mt-0.5 block text-[8.5px] text-amber-300/90">
                        {gaps.map((role) => (
                          <span key={role} className="mr-1.5 inline-block" title={`No ${sideLabels[role]} model for this property`}>
                            <span aria-hidden>●</span> <span>{sideLabels[role]} model missing</span>
                          </span>
                        ))}
                      </span>
                    ) : null}
                  </button>
                )
              })}
            </div>
            {addPropertyButton}
          </div>
          {addPropertyLine}

          {selected ? (
            <div
              role="tabpanel"
              aria-label={`${selected.name} models`}
              className="space-y-2 rounded-lg border border-amber-400/15 bg-black/25 p-2.5"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <Building2 size={13} className="shrink-0 text-amber-300" aria-hidden />
                    <h4 className="truncate text-[12px] font-semibold text-stone-100">{selected.name}</h4>
                  </div>
                  <p className="mt-0.5 flex items-center gap-1 pl-5 text-[9.5px] text-stone-500">
                    <MapPin size={9} aria-hidden /> {selected.location || 'Location not recorded'}
                    {selected.assetClass ? <span className="text-stone-600"> · {selected.assetClass}</span> : null}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => open(selected.id)}
                  aria-label={`Open ${selected.name} underwriting`}
                  title="Open this property's own underwriting workspace: comparison, evidence and review"
                  className="inline-flex items-center gap-1 text-[10px] text-amber-300 transition-colors hover:text-amber-200"
                >
                  Open property underwriting <ExternalLink size={10} aria-hidden />
                </button>
              </div>

              <div className="grid gap-2 md:grid-cols-2">
                {SIDE_ROLES.map((role) => (
                  <SideColumn
                    // Per property, so a file picked on one property is not carried to the next.
                    key={`${selected.id}:${role}`}
                    role={role}
                    property={selected}
                    properties={properties}
                    dealModels={data?.dealModels ?? []}
                    unlocked={unlocked}
                    busy={busy}
                    adding={add.isPending && add.variables?.propertyId === selected.id && add.variables?.role === role}
                    onAdd={(file) => {
                      if (file.size > MAX_WORKBOOK_UPLOAD_BYTES) {
                        setNotice(null)
                        setError(`Workbook exceeds the ${MAX_WORKBOOK_UPLOAD_BYTES / 1024 / 1024} MB upload limit.`)
                        return
                      }
                      add.mutate({
                        propertyId: selected.id,
                        propertyName: selected.name,
                        role,
                        file,
                        previous: sideModels(selected, role).find((m) => m.inRollup) ?? null,
                      })
                    }}
                    onAssign={(versionId, propertyId, withRole) =>
                      assign.mutate({
                        versionId,
                        propertyId,
                        propertyName: properties.find((p) => p.id === propertyId)?.name ?? 'the property',
                        role: withRole,
                        origin: selected.id,
                      })
                    }
                    onPlacement={(versionId, body) => placement.mutate({ versionId, body })}
                    onMove={(model, to) => move.mutate({ versionId: model.id, role: to, note: model.note })}
                    onRemove={(model) => remove.mutate({ versionId: model.id, name: model.displayName })}
                  />
                ))}
              </div>

              {selected.models.some((m) => !isSide(m.role)) ? (
                <div className="rounded-md border border-stone-800 bg-black/20 p-2">
                  <p className="mb-1 text-[9px] uppercase tracking-wider text-stone-500">No side chosen yet</p>
                  <div className="space-y-1.5">
                    {selected.models
                      .filter((m) => !isSide(m.role))
                      .map((m) => (
                        <UnassignedModel
                          key={m.id}
                          model={m}
                          property={selected}
                          unlocked={unlocked}
                          busy={busy}
                          onChoose={(to) => move.mutate({ versionId: m.id, role: to, note: m.note })}
                        />
                      ))}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}

          {data && data.excluded.length > 0 ? (
            <p className="text-[9.5px] leading-relaxed text-stone-500">
              Left out of the roll-up, dropped from the deal:{' '}
              {data.excluded.map((e) => `${e.name}${e.location ? ` (${e.location})` : ''}`).join(', ')}.
            </p>
          ) : null}
        </>
      )}

      {error ? (
        <div className="space-y-1">
          <p role="alert" className="text-[10px] text-red-300">{error}</p>
          {heldAdd ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                const held = heldAdd
                setHeldAdd(null)
                add.mutate({ ...held, useInRollup: false })
              }}
              className="rounded border border-amber-400/30 px-2 py-0.5 text-[10px] text-amber-200 hover:bg-amber-400/[0.09] disabled:opacity-40"
            >
              Add without making it the roll-up model
            </button>
          ) : null}
          {heldAssign ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                const held = heldAssign
                setHeldAssign(null)
                assign.mutate({ ...held, useInRollup: false })
              }}
              className="rounded border border-amber-400/30 px-2 py-0.5 text-[10px] text-amber-200 hover:bg-amber-400/[0.09] disabled:opacity-40"
            >
              File under {heldAssign.propertyName} without making it the roll-up model
            </button>
          ) : null}
        </div>
      ) : null}
      {notice ? <p role="status" className="text-[10px] text-emerald-300/90">{notice}</p> : null}

      {dealLevel ? (
        <details className="rounded-md border border-stone-800 bg-black/20">
          <summary className="cursor-pointer px-2.5 py-1.5 text-[10px] text-stone-400 hover:text-stone-200">
            Models filed on the portfolio itself{(data?.dealModels.length ?? 0) > 0 ? ` (${data?.dealModels.length})` : ''}
          </summary>
          <div className="space-y-1.5 border-t border-stone-800 p-2">
            <p className="text-[9.5px] leading-relaxed text-stone-500">
              A whole-portfolio workbook, or one that arrived with the package before anyone said which property it
              belongs to. File it under a property from that property’s “Choose a file already on this deal”.
            </p>
            {dealLevel}
          </div>
        </details>
      ) : null}
    </section>
  )
}

function SideColumn({
  role,
  property,
  properties,
  dealModels,
  unlocked,
  busy,
  adding,
  onAdd,
  onAssign,
  onPlacement,
  onMove,
  onRemove,
}: {
  role: SideRole
  property: PortfolioProperty
  properties: PortfolioProperty[]
  dealModels: PropertyModel[]
  unlocked: boolean
  busy: boolean
  adding: boolean
  onAdd: (file: File) => void
  onAssign: (versionId: string, propertyId: string, role?: SideRole) => void
  onPlacement: (versionId: string, body: { label?: string | null; rollupBasis?: boolean }) => void
  onMove: (model: PropertyModel, to: SideRole) => void
  onRemove: (model: PropertyModel) => void
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  // A workbook already on the deal that the person picked and has yet to confirm filing.
  const [pendingFile, setPendingFile] = useState<PropertyModel | null>(null)
  const sideLabels = useSideLabels()
  const label = sideLabels[role]
  const models = sideModels(property, role)
  const basis = models.find((m) => m.inRollup) ?? null
  // A deal-level model can be filed here when it has this side's role or none.
  const candidates = dealModels.filter((m) => m.role === role || !isSide(m.role))
  const otherProperties = properties.filter((p) => p.id !== property.id)

  return (
    <section
      aria-label={`${label} models for ${property.name}`}
      className={`rounded-md border p-2.5 ${basis ? 'border-amber-300/30 bg-amber-300/[0.025]' : 'border-stone-800 bg-black/25'}`}
    >
      <div className="mb-1.5">
        <h5 className="text-[11px] font-semibold text-stone-200">{label}</h5>
        <p className="text-[9px] text-stone-600">{SIDE_CAPTION[role]}</p>
      </div>

      {!basis ? (
        <p role="note" className="mb-2 rounded border border-amber-400/20 bg-amber-400/[0.05] px-2 py-1.5 text-[9.5px] leading-relaxed text-amber-100/90">
          {models.length === 0
            ? `No ${label} model for this property.`
            : `No ${label} model for this property can be read: ${models[0].ineligibleReason ?? 'not eligible'}.`}{' '}
          The roll-up leaves {property.name} out of the {label} figures and shows the gap. Nothing is filled in.
        </p>
      ) : null}

      <div className="space-y-1.5">
        {models.map((model) => (
          <ModelCard
            key={model.id}
            model={model}
            role={role}
            otherProperties={otherProperties}
            unlocked={unlocked}
            busy={busy}
            onAssign={(propertyId) => onAssign(model.id, propertyId)}
            onPlacement={(body) => onPlacement(model.id, body)}
            onMove={() => onMove(model, otherSide(role))}
            onRemove={() => onRemove(model)}
          />
        ))}
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,.xlsm"
          aria-label={`Upload ${label} model for ${property.name}`}
          className="hidden"
          onChange={(event) => {
            const file = event.target.files?.[0]
            event.target.value = ''
            if (file) onAdd(file)
          }}
        />
        <button
          type="button"
          aria-label={`Add UW Model (${label})`}
          disabled={!unlocked || adding}
          onClick={() => inputRef.current?.click()}
          title={unlocked ? `Add a ${label} underwriting model to ${property.name}` : 'Unlock the dossier to add a model'}
          className="inline-flex items-center gap-1 rounded border border-amber-400/30 px-2.5 py-1 text-[10px] font-medium text-amber-200 transition-colors hover:bg-amber-400/[0.09] disabled:cursor-not-allowed disabled:border-stone-800 disabled:text-stone-600"
        >
          {adding ? <Loader2 size={11} className="animate-spin" aria-hidden /> : unlocked ? <Plus size={11} aria-hidden /> : <LockKeyhole size={11} aria-hidden />}
          {adding ? 'Parsing…' : 'Add UW Model'}
        </button>
        {candidates.length > 0 ? (
          <select
            aria-label={`Choose a file already on this deal for ${label}`}
            value=""
            disabled={!unlocked || busy}
            // Choosing from the list only names the file: a keyboard arrow passes over the
            // options, so filing waits for a confirmation below.
            onChange={(event) => setPendingFile(candidates.find((m) => m.id === event.target.value) ?? null)}
            className="max-w-[210px] rounded border border-stone-700 bg-[#0c0c0c] px-1.5 py-1 text-[9.5px] text-stone-300 outline-none focus:border-amber-300/50 disabled:opacity-50"
          >
            <option value="">or a file already on this deal…</option>
            {candidates.map((m) => (
              <option key={m.id} value={m.id}>
                v{m.versionNumber} · {m.displayName}
              </option>
            ))}
          </select>
        ) : null}
      </div>

      {pendingFile ? (
        <div
          role="group"
          aria-label="Confirm filing"
          className="mt-1.5 rounded border border-amber-400/25 bg-amber-400/[0.05] p-1.5 text-[9.5px] leading-relaxed text-amber-100/90"
        >
          File {pendingFile.displayName} under {property.name} as its {label} model? It is filed through the ordinary
          upload path, and the original on the portfolio is withdrawn, which takes back what it put on the portfolio’s
          own record: {takenBackWords(pendingFile.role)}. It stays on file as evidence.
          <div className="mt-1 flex gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                const picked = pendingFile
                setPendingFile(null)
                onAssign(picked.id, property.id, role)
              }}
              className="rounded border border-amber-400/40 px-1.5 py-0.5 text-[9px] text-amber-200 hover:bg-amber-400/10 disabled:opacity-40"
            >
              Confirm filing
            </button>
            <button type="button" autoFocus onClick={() => setPendingFile(null)} className="text-[9px] text-stone-400 hover:text-stone-200">
              Cancel
            </button>
          </div>
        </div>
      ) : null}
    </section>
  )
}

/** What putting a model that has no side on one does, said before it is done. */
function sideChoiceWords(model: PropertyModel, property: PortfolioProperty, to: SideRole, labels: SideLabels): string {
  const question = `Make ${model.displayName} the ${labels[to]} model of ${property.name}?`
  return to === 'sponsor'
    ? `${question} It is filed on the Sponsor side: the roll-up may read it for the Sponsor figures, and it fills any figure the property record is missing.`
    : `${question} It is filed on the ${labels.underwriter} side: the roll-up may read it for the ${labels.underwriter} figures.`
}

/** A model that has no side yet, with the choice of one. Choosing only asks: filing waits for a confirmation. */
function UnassignedModel({
  model,
  property,
  unlocked,
  busy,
  onChoose,
}: {
  model: PropertyModel
  property: PortfolioProperty
  unlocked: boolean
  busy: boolean
  onChoose: (to: SideRole) => void
}) {
  const [choosing, setChoosing] = useState<SideRole | null>(null)
  const sideLabels = useSideLabels()
  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center justify-between gap-2 text-[10px] text-stone-300">
        <span className="truncate" title={model.filename}>{model.displayName}</span>
        <span className="flex gap-1.5">
          {SIDE_ROLES.map((to) => (
            <button
              key={to}
              type="button"
              disabled={!unlocked || busy}
              onClick={() => setChoosing(to)}
              className="rounded border border-stone-700 px-1.5 py-0.5 text-[9px] text-stone-300 hover:border-amber-400/40 hover:text-amber-200 disabled:opacity-40"
            >
              This is the {sideLabels[to]} model
            </button>
          ))}
        </span>
      </div>
      {choosing ? (
        <div
          role="group"
          aria-label="Confirm side choice"
          className="rounded border border-amber-400/25 bg-amber-400/[0.05] p-1.5 text-[9.5px] leading-relaxed text-amber-100/90"
        >
          {sideChoiceWords(model, property, choosing, sideLabels)}
          <div className="mt-1 flex gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                const side = choosing
                setChoosing(null)
                onChoose(side)
              }}
              className="rounded border border-amber-400/40 px-1.5 py-0.5 text-[9px] text-amber-200 hover:bg-amber-400/10 disabled:opacity-40"
            >
              Confirm side
            </button>
            <button type="button" autoFocus onClick={() => setChoosing(null)} className="text-[9px] text-stone-400 hover:text-stone-200">
              Cancel
            </button>
          </div>
        </div>
      ) : null}
    </div>
  )
}

function ModelCard({
  model,
  role,
  otherProperties,
  unlocked,
  busy,
  onAssign,
  onPlacement,
  onMove,
  onRemove,
}: {
  model: PropertyModel
  role: SideRole
  otherProperties: PortfolioProperty[]
  unlocked: boolean
  busy: boolean
  onAssign: (propertyId: string) => void
  onPlacement: (body: { label?: string | null; rollupBasis?: boolean }) => void
  onMove: () => void
  onRemove: () => void
}) {
  const sideLabels = useSideLabels()
  const [renaming, setRenaming] = useState(false)
  const [draft, setDraft] = useState(model.label ?? '')
  const [confirming, setConfirming] = useState(false)
  // A move waiting for its confirmation: to the other side, or to another property.
  const [moving, setMoving] = useState<{ kind: 'side' } | { kind: 'property'; property: PortfolioProperty } | null>(null)
  const lockedTitle = unlocked ? undefined : 'Unlock the dossier to change this model'
  const action =
    'rounded border border-transparent px-1 py-0.5 text-[9px] text-stone-400 transition-colors hover:text-amber-200 disabled:cursor-not-allowed disabled:text-stone-700 disabled:hover:text-stone-700'

  return (
    <article
      aria-label={model.displayName}
      className={`rounded border px-2 py-1.5 ${model.inRollup ? 'border-emerald-500/25 bg-emerald-500/[0.03]' : 'border-stone-800 bg-black/20'}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate text-[11px] font-semibold text-stone-200" title={model.displayName}>
            {model.displayName}
          </div>
          {model.label ? (
            <div className="truncate text-[9px] text-stone-600" title={model.filename}>{model.filename}</div>
          ) : null}
        </div>
        <span
          className={`shrink-0 rounded border px-1.5 py-0.5 text-[8px] uppercase tracking-widest ${STATUS_STYLE[model.status] ?? 'border-stone-700 text-stone-400'}`}
        >
          {model.status}
        </span>
      </div>

      <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[9px] text-stone-500">
        <span>v{model.versionNumber}</span>
        <span className={model.mappedCellCount === 0 ? 'text-amber-300' : ''}>
          {model.mappedCellCount.toLocaleString()} / {model.importedCellCount.toLocaleString()} cells mapped
        </span>
        <span className="text-stone-600">
          {model.uploadedBy}
          {model.uploadedAt ? ` · ${humanDate(model.uploadedAt)}` : ''}
        </span>
      </div>

      <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[9px]">
        {model.inRollup ? (
          <span className="rounded bg-emerald-500/10 px-1.5 py-0.5 text-emerald-300">In roll-up</span>
        ) : (
          <span className="rounded bg-stone-800/70 px-1.5 py-0.5 text-stone-400">Not in roll-up</span>
        )}
        {!model.eligible ? (
          <span className="text-red-300" title="The roll-up cannot read this model">{model.ineligibleReason}</span>
        ) : null}
        {model.eligible && model.mappedCellCount === 0 ? (
          <span className="text-amber-300">Nothing in it is mapped, so no figure reaches the roll-up</span>
        ) : null}
      </div>

      {model.sharedWith.length > 0 ? (
        <p role="note" className="mt-1 text-[9px] leading-relaxed text-amber-300/90">
          Same file as the {sideLabels[role]} model of {model.sharedWith.map((s) => s.name).join(', ')}: the roll-up counts it
          for none of them. Remove one, or give each property its own model.
        </p>
      ) : null}

      {renaming ? (
        <form
          className="mt-1.5 flex items-center gap-1.5"
          onSubmit={(event) => {
            event.preventDefault()
            onPlacement({ label: draft.trim() ? draft.trim() : null })
            setRenaming(false)
          }}
        >
          <input
            aria-label={`Label for ${model.displayName}`}
            value={draft}
            maxLength={160}
            autoFocus
            onChange={(event) => setDraft(event.target.value)}
            placeholder={model.filename}
            className="min-w-0 flex-1 rounded border border-stone-700 bg-black/40 px-1.5 py-0.5 text-[10px] text-stone-200 outline-none focus:border-amber-300/50"
          />
          <button type="submit" disabled={busy} className="rounded border border-amber-400/30 px-1.5 py-0.5 text-[9px] text-amber-200 disabled:opacity-40">
            Save label
          </button>
          <button type="button" onClick={() => setRenaming(false)} className="text-[9px] text-stone-500 hover:text-stone-300">
            Cancel
          </button>
        </form>
      ) : null}

      {confirming ? (
        <div
          role="group"
          aria-label="Confirm removal"
          className="mt-1.5 rounded border border-red-500/25 bg-red-500/[0.05] p-1.5 text-[9.5px] leading-relaxed text-red-100/90"
        >
          Remove this model from the portfolio? It stays on file as evidence. Removing it takes back what it put on this
          property’s record: {takenBackWords(role)}. The record is then read again from the property’s other models.
          <div className="mt-1 flex gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setConfirming(false)
                onRemove()
              }}
              className="rounded border border-red-500/40 px-1.5 py-0.5 text-[9px] text-red-200 hover:bg-red-500/10 disabled:opacity-40"
            >
              Confirm remove
            </button>
            <button type="button" autoFocus onClick={() => setConfirming(false)} className="text-[9px] text-stone-400 hover:text-stone-200">
              Cancel
            </button>
          </div>
        </div>
      ) : moving ? (
        <div
          role="group"
          aria-label="Confirm move"
          className="mt-1.5 rounded border border-amber-400/25 bg-amber-400/[0.05] p-1.5 text-[9.5px] leading-relaxed text-amber-100/90"
        >
          {moving.kind === 'side' ? moveSideWords(role, sideLabels) : moveToPropertyWords(moving.property, role)}
          <div className="mt-1 flex gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                const confirmed = moving
                setMoving(null)
                if (confirmed.kind === 'side') onMove()
                else onAssign(confirmed.property.id)
              }}
              className="rounded border border-amber-400/40 px-1.5 py-0.5 text-[9px] text-amber-200 hover:bg-amber-400/10 disabled:opacity-40"
            >
              Confirm move
            </button>
            <button type="button" autoFocus onClick={() => setMoving(null)} className="text-[9px] text-stone-400 hover:text-stone-200">
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-1 flex flex-wrap items-center gap-x-1 gap-y-0.5 border-t border-stone-800/70 pt-1">
          <button
            type="button"
            disabled={!unlocked || busy}
            title={lockedTitle ?? 'Give this model a name that tells it from the others'}
            onClick={() => {
              setDraft(model.label ?? '')
              setRenaming(true)
            }}
            className={action}
          >
            <Pencil size={9} className="mr-0.5 inline" aria-hidden />
            Rename
          </button>
          {model.eligible && !model.inRollup ? (
            <button
              type="button"
              disabled={!unlocked || busy}
              title={lockedTitle ?? 'Have the roll-up read this model instead of the one it reads now'}
              onClick={() => onPlacement({ rollupBasis: true })}
              className={action}
            >
              Use in roll-up
            </button>
          ) : null}
          <button
            type="button"
            disabled={!unlocked || busy}
            title={lockedTitle ?? `File this model on the ${sideLabels[otherSide(role)]} side instead`}
            onClick={() => setMoving({ kind: 'side' })}
            className={action}
          >
            <ArrowRightLeft size={9} className="mr-0.5 inline" aria-hidden />
            Move to {sideLabels[otherSide(role)]}
          </button>
          {otherProperties.length > 0 ? (
            <select
              aria-label={`Move ${model.displayName} to another property`}
              value=""
              disabled={!unlocked || busy}
              title={lockedTitle ?? 'File this model under another property of the portfolio'}
              // Choosing from the list only names the property: a keyboard arrow passes over
              // the options, so the move waits for a confirmation.
              onChange={(event) => {
                const target = otherProperties.find((p) => p.id === event.target.value)
                if (target) setMoving({ kind: 'property', property: target })
              }}
              className="rounded border border-transparent bg-transparent px-0.5 py-0.5 text-[9px] text-stone-400 outline-none hover:text-amber-200 focus:border-stone-700 disabled:cursor-not-allowed disabled:text-stone-700"
            >
              <option value="">Move to property…</option>
              {otherProperties.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          ) : null}
          <button
            type="button"
            disabled={!unlocked || busy}
            title={lockedTitle ?? 'Remove this model from the portfolio (it stays on file as evidence)'}
            onClick={() => setConfirming(true)}
            className={`${action} ml-auto hover:text-red-300`}
          >
            <Trash2 size={9} className="mr-0.5 inline" aria-hidden />
            Remove
          </button>
        </div>
      )}
    </article>
  )
}
